"use client"

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { Camera, Video, Square } from 'lucide-react'

/** Set NEXT_PUBLIC_DEBUG_CAMERA=true in .env.local to enable verbose camera/recording logs */
const DEBUG_CAMERA = typeof process !== 'undefined' && process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DEBUG_CAMERA === 'true'

interface CameraCaptureModalProps {
    isOpen: boolean
    onClose: () => void
    onCapture: (file: File) => void
    lastCapturedThumbnail?: string | null // Thumbnail URL of last captured photo/video
    hideDone?: boolean // Hide Done button when description popup is active
}

type CaptureMode = 'photo' | 'video'

/**
 * Camera capture interface component
 * Opens device camera and allows photo capture and video recording
 */
export default function CameraCaptureModal({
    isOpen,
    onClose,
    onCapture,
    lastCapturedThumbnail,
    hideDone = false,
}: CameraCaptureModalProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const recordedChunksRef = useRef<Blob[]>([])
    const [hasPermission, setHasPermission] = useState<boolean | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [captureMode, setCaptureMode] = useState<CaptureMode>('photo')
    const [isRecording, setIsRecording] = useState(false)
    const [recordingTime, setRecordingTime] = useState(0)
    const recordingTimerRef = useRef<NodeJS.Timeout | null>(null)
    const recordingStartTimeRef = useRef<number | null>(null) // Track when recording started

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current
        const isActuallyRecording = recorder?.state === 'recording'
        if (recorder && isActuallyRecording) {
            const currentChunksSize = recordedChunksRef.current.reduce((sum, chunk) => sum + (chunk?.size || 0), 0)
            const timeSinceStart = recordingStartTimeRef.current
                ? (Date.now() - recordingStartTimeRef.current) / 1000
                : 0

            if (DEBUG_CAMERA) console.log('🛑 stopRecording called:', {
                recorderState: recorder.state,
                chunksCount: recordedChunksRef.current.length,
                totalChunksSize: currentChunksSize,
                timeSinceStart: timeSinceStart.toFixed(2) + 's'
            })

            // Prevent stopping if recording just started (< 100ms ago) - likely accidental
            if (timeSinceStart < 0.1) {
                console.warn('⚠️ Recording stopped immediately after starting (< 100ms). Ignoring stop request.')
                return
            }

            if (timeSinceStart < 1) {
                console.warn('⚠️ Recording stopped very quickly (< 1 second). Data may not be available yet.')
            }

            // Request any remaining data before stopping (ensures we get the final chunk)
            if (recorder.state === 'recording') {
                if (DEBUG_CAMERA) console.log('📥 Requesting final data chunk before stopping...')
                try {
                    recorder.requestData()
                } catch (error) {
                    console.warn('⚠️ Error requesting data:', error)
                }
            }

            // Small delay to ensure requestData() is processed, then stop
            setTimeout(() => {
                if (mediaRecorderRef.current?.state === 'recording') {
                    if (DEBUG_CAMERA) console.log('🛑 Stopping MediaRecorder...')
                    try {
                        mediaRecorderRef.current.stop()
                        if (DEBUG_CAMERA) console.log('🛑 MediaRecorder.stop() called, new state:', mediaRecorderRef.current.state)
                    } catch (error) {
                        console.error('❌ Error stopping MediaRecorder:', error)
                    }
                } else {
                    if (DEBUG_CAMERA) console.log('📝 MediaRecorder already stopped or not in recording state')
                }
            }, 100)

            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current)
                recordingTimerRef.current = null
            }
        } else {
            console.warn('⚠️ stopRecording called but recorder not active:', {
                hasRecorder: !!recorder,
                recorderState: recorder?.state
            })
        }
    }, [])

    const stopCamera = useCallback(async () => {
        const recorderState = mediaRecorderRef.current?.state
        if (DEBUG_CAMERA) console.log('📷 stopCamera called:', {
            hasRecorder: !!mediaRecorderRef.current,
            recorderState,
            hasStream: !!streamRef.current
        })
        
        // Stop recording if active - but wait for it to complete before stopping the stream
        // Use recorder state from ref, not isRecording, so this callback stays stable
        if (mediaRecorderRef.current && recorderState === 'recording') {
            if (DEBUG_CAMERA) console.log('⏸️ Recording in progress, stopping recording first...')
            // Wait for the onstop promise if it exists
            const onStopPromise = (mediaRecorderRef.current as any)?._onStopPromise
            if (onStopPromise) {
                try {
                    stopRecording()
                    await Promise.race([
                        onStopPromise,
                        new Promise<void>((resolve) => setTimeout(() => {
                            console.warn('⚠️ Recording stop timeout (2s), proceeding anyway')
                            resolve()
                        }, 2000))
                    ])
                    if (DEBUG_CAMERA) console.log('✅ Recording stopped, now stopping camera stream')
                } catch (error) {
                    console.error('❌ Error waiting for recording to stop:', error)
                }
            } else {
                // Fallback: just stop and wait a bit
                stopRecording()
                await new Promise(resolve => setTimeout(resolve, 500))
            }
        }
        
        // CRITICAL: Never stop the stream while recording is active!
        // Stopping the stream will immediately stop the MediaRecorder
        const finalRecorderState = mediaRecorderRef.current?.state
        if (finalRecorderState === 'recording') {
            console.error('❌ ERROR: Attempting to stop stream while recording is still active! This will corrupt the recording.')
            console.error('   Aborting stream stop to preserve recording. Recording must be stopped first.')
            return // Don't stop the stream - let the recording finish naturally
        }
        
        // Now safe to stop the stream (recording is definitely stopped)
        if (streamRef.current) {
            if (DEBUG_CAMERA) console.log('🛑 Stopping camera stream...')
            streamRef.current.getTracks().forEach((track) => {
                // Don't stop tracks that are still active (recording might still be using them)
                if (track.readyState === 'live') {
                    if (DEBUG_CAMERA) console.log(`  📹 Stopping live track: ${track.kind} (${track.label})`)
                    track.stop()
                } else {
                    if (DEBUG_CAMERA) console.log(`  📹 Track already stopped: ${track.kind} (${track.label})`)
                }
            })
            streamRef.current = null
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null
        }
    }, [stopRecording])

    const startCamera = async () => {
        try {
            setError(null)
            // Request both video and audio for video recording
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment', // Use rear camera on mobile
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: true, // Enable audio for video recording
            })
            streamRef.current = stream
            if (videoRef.current) {
                videoRef.current.srcObject = stream
                setHasPermission(true)
            }
        } catch (err) {
            console.error('Error accessing camera:', err)
            setHasPermission(false)
            setError(
                err instanceof Error
                    ? err.message
                    : 'Unable to access camera. Please check permissions.'
            )
        }
    }

    useEffect(() => {
        if (isOpen) {
            if (DEBUG_CAMERA) console.log('📷 Camera modal opened, starting camera...')
            startCamera()
        } else {
            if (DEBUG_CAMERA) console.log('📷 Camera modal closed, stopping camera...')
            stopCamera().catch(error => {
                console.error('Error stopping camera:', error)
            })
        }

        return () => {
            // Cleanup only on unmount or when isOpen changes. Do NOT include isRecording
            // as a dependency — starting recording would re-run this effect, trigger cleanup,
            // and stop the recording immediately.
            const recorderState = mediaRecorderRef.current?.state
            const isActuallyRecording = recorderState === 'recording'
            if (DEBUG_CAMERA) console.log('🧹 Cleanup function called:', {
                recorderState,
                isActuallyRecording,
                hasRecorder: !!mediaRecorderRef.current,
                hasStream: !!streamRef.current
            })
            if (isActuallyRecording) {
                console.warn('⚠️ Unmounting during active recording, will stop after recording completes')
            }
            stopCamera().catch(error => {
                console.error('Error stopping camera in cleanup:', error)
            })
        }
    }, [isOpen, stopCamera])

    const handleCapture = () => {
        if (!videoRef.current) return

        const canvas = document.createElement('canvas')
        const video = videoRef.current
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.drawImage(video, 0, 0)

        canvas.toBlob(
            (blob) => {
                if (!blob) return

                const file = new File([blob], `photo-${Date.now()}.jpg`, {
                    type: 'image/jpeg',
                })

                // Don't stop camera or close - keep camera open for more photos
                onCapture(file)
            },
            'image/jpeg',
            0.9
        )
    }

    const startRecording = () => {
        if (!streamRef.current || !videoRef.current) {
            console.error('❌ Cannot start recording: stream or video ref not available', {
                hasStream: !!streamRef.current,
                hasVideo: !!videoRef.current
            })
            return
        }
        
        // Check if already recording
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            console.warn('⚠️ Already recording! Ignoring startRecording call.')
            return
        }

        try {
            if (DEBUG_CAMERA) console.log('🎬 Starting video recording...', {
                streamActive: streamRef.current.active,
                streamTracks: streamRef.current.getTracks().length,
                videoReady: videoRef.current.readyState >= 2
            })
            recordedChunksRef.current = []
            
            // Determine MIME type based on browser support
            let mimeType = 'video/webm'
            const supportedTypes = [
                'video/mp4',
                'video/webm;codecs=vp9',
                'video/webm;codecs=vp8',
                'video/webm'
            ]
            
            if (DEBUG_CAMERA) {
                console.log('🔍 Checking MIME type support:')
                supportedTypes.forEach(type => {
                    const supported = MediaRecorder.isTypeSupported(type)
                    console.log(`  ${supported ? '✅' : '❌'} ${type}: ${supported}`)
                })
            }
            
            if (MediaRecorder.isTypeSupported('video/mp4')) {
                mimeType = 'video/mp4'
            } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
                mimeType = 'video/webm;codecs=vp9'
            } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
                mimeType = 'video/webm;codecs=vp8'
            }
            
            if (DEBUG_CAMERA) console.log('📹 Selected MIME type:', mimeType)

            const mediaRecorder = new MediaRecorder(streamRef.current, {
                mimeType,
                videoBitsPerSecond: 2500000, // 2.5 Mbps for good quality
            })
            
            if (DEBUG_CAMERA) console.log('🎥 MediaRecorder created:', {
                mimeType: mediaRecorder.mimeType,
                state: mediaRecorder.state,
                videoBitsPerSecond: mediaRecorder.videoBitsPerSecond,
                audioBitsPerSecond: mediaRecorder.audioBitsPerSecond
            })

            mediaRecorder.ondataavailable = (event) => {
                const chunkSize = event.data?.size || 0
                const willAdd = chunkSize > 0
                if (DEBUG_CAMERA) console.log('🎥 MediaRecorder data available:', {
                    size: chunkSize,
                    type: event.data?.type || 'unknown',
                    willAdd,
                    currentChunksCount: recordedChunksRef.current.length,
                    totalChunksSize: recordedChunksRef.current.reduce((sum, chunk) => sum + (chunk?.size || 0), 0)
                })
                
                // Always add the chunk if it exists, even if size is 0
                // Empty chunks can be valid (first chunk is often empty, final chunk after stop might be empty)
                // We'll filter out truly empty blobs when creating the final blob
                if (event.data) {
                    recordedChunksRef.current.push(event.data)
                    if (DEBUG_CAMERA && chunkSize === 0) console.log('📝 Note: Empty chunk added (this is normal for first/last chunks)')
                } else {
                    console.warn('⚠️ No data object in event, skipping chunk')
                }
            }

            // Store the onstop handler so we can call it from stopCamera if needed
            let onStopResolve: (() => void) | null = null
            const onStopPromise = new Promise<void>((resolve) => {
                onStopResolve = resolve
            })
            
            mediaRecorder.onstop = () => {
                const totalChunksSize = recordedChunksRef.current.reduce((sum, chunk) => sum + (chunk?.size || 0), 0)
                if (DEBUG_CAMERA) console.log('🛑 MediaRecorder stopped:', {
                    chunksCount: recordedChunksRef.current.length,
                    totalChunksSize,
                    mimeType,
                    recorderState: mediaRecorder.state
                })
                
                if (DEBUG_CAMERA) recordedChunksRef.current.forEach((chunk, index) => {
                    console.log(`  📦 Chunk ${index + 1}:`, { size: chunk.size, type: chunk.type })
                })
                
                // Filter out empty chunks (but keep at least one chunk if all are empty, for error handling)
                const nonEmptyChunks = recordedChunksRef.current.filter(chunk => chunk.size > 0)
                const hasData = nonEmptyChunks.length > 0 || totalChunksSize > 0
                
                if (DEBUG_CAMERA) console.log('🔍 Chunk analysis:', {
                    totalChunks: recordedChunksRef.current.length,
                    nonEmptyChunks: nonEmptyChunks.length,
                    hasData,
                    totalSize: totalChunksSize
                })
                
                // Small delay to ensure all data is finalized (especially important for MP4)
                // MP4 files need the moov atom which is written at the end
                setTimeout(() => {
                    // Use all chunks (including empty ones) - the browser handles this correctly
                    // But verify we have actual data
                    const finalChunks = recordedChunksRef.current.length > 0 
                        ? recordedChunksRef.current 
                        : []
                    
                    const blob = new Blob(finalChunks, { type: mimeType })
                    if (DEBUG_CAMERA) console.log('📹 Blob created:', {
                        size: blob.size,
                        type: blob.type,
                        chunksUsed: finalChunks.length,
                        expectedSize: totalChunksSize,
                        sizeMatch: blob.size === totalChunksSize
                    })
                    
                    // Verify blob is not empty
                    if (blob.size === 0) {
                        console.error('❌ ERROR: Blob is empty! Cannot create file.')
                        console.error('   This usually means recording was stopped too quickly (< 1 second)')
                        console.error('   or no data chunks were collected during recording.')
                        setError('Video recording failed: no data captured. Please record for at least 1 second.')
                        setIsRecording(false)
                        setRecordingTime(0)
                        recordingStartTimeRef.current = null // Reset start time
                        if (onStopResolve) onStopResolve()
                        return
                    }
                    
                    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm'
                    const fileName = `video-${Date.now()}.${extension}`
                    const file = new File([blob], fileName, {
                        type: mimeType,
                    })
                    
                    if (DEBUG_CAMERA) console.log('📁 File created from blob:', {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        lastModified: new Date(file.lastModified).toISOString()
                    })
                    
                    // Additional verification: try to read blob as array buffer to ensure it's valid
                    const reader = new FileReader()
                    reader.onloadend = () => {
                        const arrayBufferSize = reader.result instanceof ArrayBuffer ? reader.result.byteLength : 0
                        const sizeMatch = arrayBufferSize === blob.size
                        if (DEBUG_CAMERA) console.log('✅ Blob verification complete:', {
                            arrayBufferSize,
                            blobSize: blob.size,
                            match: sizeMatch
                        })
                        
                        if (!sizeMatch) {
                            console.error('❌ WARNING: Blob size mismatch! ArrayBuffer size does not match blob size.')
                        }
                    }
                    reader.onerror = (error) => {
                        console.error('❌ ERROR reading blob for verification:', error)
                    }
                    reader.readAsArrayBuffer(blob)
                    
                    if (DEBUG_CAMERA) console.log('📤 Calling onCapture with file...')
                    onCapture(file)
                    setIsRecording(false)
                    setRecordingTime(0)
                    recordingStartTimeRef.current = null // Reset start time
                    if (onStopResolve) onStopResolve()
                }, 100) // 100ms delay to ensure blob is fully finalized
            }
            
            // Store the promise so stopCamera can wait for it
            ;(mediaRecorder as any)._onStopPromise = onStopPromise
            
            mediaRecorder.onerror = (event) => {
                console.error('❌ MediaRecorder error:', event)
                setError('Video recording error occurred')
            }

            mediaRecorderRef.current = mediaRecorder
            // Use a smaller timeslice (100ms) to collect data more frequently
            // This ensures we get data even for short recordings
            // The browser will still optimize, but we'll get more frequent updates
            const timeslice = 100 // 100ms timeslice for more frequent data collection
            if (DEBUG_CAMERA) console.log(`▶️ Starting MediaRecorder with ${timeslice}ms timeslice...`)
            mediaRecorder.start(timeslice) // Collect data every 100ms
            if (DEBUG_CAMERA) console.log('✅ MediaRecorder started, state:', mediaRecorder.state)
            setIsRecording(true)
            setRecordingTime(0)
            recordingStartTimeRef.current = Date.now() // Record when recording started

            // Start timer - use absolute time to track duration so it persists across remounts
            const timerStartTime = Date.now()
            recordingTimerRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - timerStartTime) / 1000)
                setRecordingTime(elapsed)
            }, 100) // Update every 100ms for smoother display
        } catch (err) {
            console.error('Error starting video recording:', err)
            setError('Failed to start video recording. Please try again.')
        }
    }

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current)
            }
        }
    }, [])

    if (!isOpen) return null

    // Prevent closing dialog while recording
    const handleOpenChange = (open: boolean) => {
        if (!open && isRecording) {
            console.warn('⚠️ Attempted to close dialog while recording. Preventing close.')
            // Don't close - user must stop recording first
            return
        }
        if (!open) {
            onClose()
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-full w-full h-full max-h-full p-0 gap-0 bg-black"
                hideCloseButton
            >
                <DialogTitle className="sr-only">Camera Capture</DialogTitle>
                <DialogDescription className="sr-only">Take a photo or record a video using your device camera</DialogDescription>
                <div className="relative w-full h-full flex flex-col">
                    {/* Mode toggle (top) */}
                    {hasPermission && (
                        <div className="absolute top-4 left-0 right-0 flex justify-center z-20">
                            <div className="flex gap-2 bg-black/50 rounded-full p-1">
                                <Button
                                    onClick={() => setCaptureMode('photo')}
                                    size="sm"
                                    variant={captureMode === 'photo' ? 'default' : 'ghost'}
                                    className={cn(
                                        "rounded-full",
                                        captureMode === 'photo' 
                                            ? "bg-primary text-white hover:bg-primary/90" 
                                            : "text-white hover:bg-white/20"
                                    )}
                                >
                                    <Camera className="w-4 h-4 mr-1" />
                                    Photo
                                </Button>
                                <Button
                                    onClick={() => {
                                        if (isRecording) {
                                            stopRecording()
                                        }
                                        setCaptureMode('video')
                                    }}
                                    size="sm"
                                    variant={captureMode === 'video' ? 'default' : 'ghost'}
                                    className={cn(
                                        "rounded-full",
                                        captureMode === 'video' 
                                            ? "bg-primary text-white hover:bg-primary/90" 
                                            : "text-white hover:bg-white/20"
                                    )}
                                >
                                    <Video className="w-4 h-4 mr-1" />
                                    Video
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Recording timer */}
                    {isRecording && (
                        <div className="absolute top-16 left-0 right-0 flex justify-center z-20">
                            <div className="bg-red-600 text-white px-4 py-2 rounded-full flex items-center gap-2">
                                <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                                <span className="font-mono font-bold">{formatTime(recordingTime)}</span>
                            </div>
                        </div>
                    )}

                    {/* Video preview */}
                    <div className="flex-1 relative overflow-hidden">
                        {hasPermission === false || error ? (
                            <div className="flex items-center justify-center h-full text-white p-4">
                                <div className="text-center">
                                    <p className="text-lg mb-2">Camera access denied</p>
                                    <p className="text-sm text-gray-400">
                                        {error ||
                                            'Please enable camera permissions in your browser settings.'}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted={captureMode === 'photo' || isRecording}
                                className="w-full h-full object-cover"
                            />
                        )}
                    </div>

                    {/* Bottom controls: thumbnail, capture button, Save button */}
                    {hasPermission && (
                        <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center px-4 z-10">
                            {/* Last captured thumbnail - left of capture button */}
                            {lastCapturedThumbnail && (
                                <div className="absolute right-1/2 w-12 h-12 rounded-full border-2 border-white overflow-hidden bg-black mr-16">
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={lastCapturedThumbnail}
                                        alt="Last captured media"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}
                            
                            {/* Capture/Record button (centered) */}
                            {captureMode === 'photo' ? (
                                <Button
                                    onClick={handleCapture}
                                    size="lg"
                                    className="rounded-full w-20 h-20 bg-primary hover:bg-primary/90 border-4 border-white/90 shadow-lg flex-shrink-0"
                                    aria-label="Capture photo"
                                />
                            ) : (
                                <>
                                    {!isRecording ? (
                                        <Button
                                            onClick={startRecording}
                                            size="lg"
                                            className="rounded-full w-20 h-20 bg-red-600 hover:bg-red-700 border-4 border-white shadow-lg flex-shrink-0"
                                            aria-label="Start recording video"
                                        />
                                    ) : (
                                        <Button
                                            onClick={stopRecording}
                                            size="lg"
                                            className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700 border-4 border-white shadow-lg flex-shrink-0"
                                            aria-label="Stop recording video"
                                        >
                                            <Square className="w-6 h-6 fill-white" />
                                        </Button>
                                    )}
                                </>
                            )}
                            
                            {/* Right side: Save button - fixed distance from shutter */}
                            {!hideDone && !isRecording && (
                                <Button
                                    onClick={onClose}
                                    size="default"
                                    className="absolute left-1/2 ml-[80px] px-5 py-2.5 rounded-full bg-primary hover:bg-primary/90 text-white shadow-lg"
                                    aria-label="Save"
                                >
                                    Save
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

